import { type ReactNode, useEffect, useRef, useState } from "react";
import { ChevronUp } from "lucide-react";

/**
 * The chevron beside a control, and what it opens.
 *
 * Everything a control needs but is not the control itself lives here: which
 * microphone, which camera, whether the background is blurred, whether a share
 * carries only its sound. Grouping them this way is what took the bar from
 * eight peers competing for one glance down to five things a session actually
 * uses, without hiding anything behind a "more" that says nothing about what
 * is inside it.
 *
 * The chevron shares the control's border, so the pair reads as one control
 * with a second half rather than as two buttons that happen to be adjacent.
 *
 * Children are given a `close` to call rather than the panel closing itself
 * on any click inside it. That shortcut broke every choice in here: a click on
 * a row bubbles to the panel *before* the browser forwards it from the label
 * to the radio inside, so the panel unmounted the input and the change it was
 * about to make never happened. Closing is now each control's own decision,
 * taken after its own state has moved — and a toggle can decline to close at
 * all, so the state it just changed stays on screen.
 */
export default function MeetingControlMenu({
    label,
    testId,
    icon,
    active = false,
    compact = false,
    children,
}: {
    /** Names what the menu opens. Read out for a chevron, shown under an icon. */
    label: string;
    testId: string;
    /**
     * Given for a menu that stands on its own in the bar rather than beside a
     * control — the layout, which modifies nothing but the picture.
     */
    icon?: ReactNode;
    active?: boolean;
    /** The phone's version: the icon alone, and the panel as a sheet. */
    compact?: boolean;
    children: (close: () => void) => ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const container = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };
        const onPointerDown = (event: PointerEvent) => {
            if (!container.current?.contains(event.target as Node)) setOpen(false);
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, [open]);

    const trigger = icon ? (
        <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-haspopup="true"
            data-testid={testId}
            className={`room-control ${
                compact ? "room-control-icon" : "room-control-stack"
            } ${active || open ? "room-control-on" : ""}`}
        >
            {icon}
            <span className={compact ? "sr-only" : undefined}>{label}</span>
        </button>
    ) : (
        <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-haspopup="true"
            aria-label={label}
            data-testid={testId}
            /* Four pixels narrower on a phone. It reads as one control with
               the toggle, so it is not a target of its own to size — and
               those four pixels twice are what let the whole bar sit on one
               row at 390px instead of wrapping. */
            className={`flex items-center justify-center text-room-ink transition-colors duration-[--duration-fast] hover:bg-room-hover ${
                compact ? "w-5" : "w-6"
            }`}
        >
            <ChevronUp size={14} aria-hidden="true" />
        </button>
    );

    return (
        <div
            // The hairline and the shared rounding belong to a chevron sitting
            // inside a control group, not to a menu standing on its own.
            className={`relative flex ${icon ? "" : "room-control-chevron"}`}
            ref={container}
        >
            {trigger}

            {open && (
                <div
                    data-testid={`${testId}-panel`}
                    /* Centred over its own control on a laptop. On a phone
                       that is how a 256px panel ends up half off the screen:
                       the controls sit at the edges of a 390px row, and there
                       is nothing to centre it over that fits. So there it
                       spans the width above the bar instead, which is where a
                       phone puts this kind of panel anyway. */
                    className={`z-20 flex flex-col gap-0.5 rounded-xl border border-room-line bg-room-raised p-2 text-left shadow-lg ${
                        compact
                            ? "fixed inset-x-2 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] max-h-[60vh] overflow-y-auto"
                            : "absolute bottom-full left-1/2 mb-2 w-64 -translate-x-1/2"
                    }`}
                >
                    {children(() => setOpen(false))}
                </div>
            )}
        </div>
    );
}
